import type { HyperliquidConfig } from '@/lib/utils/hyper'
import { Hyperliquid } from '@/lib/utils/hyper'

/**
 * Hyperliquid SDK 单例管理器
 * 用于管理全局唯一的 Hyperliquid 实例，提供同步的客户端访问
 */
class HyperliquidSDKManager {
  private static instance: HyperliquidSDKManager
  private hyperliquid: Hyperliquid | null = null
  private currentConfig: HyperliquidConfig = {}

  private constructor() {}

  /**
   * 获取单例实例
   */
  public static getInstance(): HyperliquidSDKManager {
    if (!HyperliquidSDKManager.instance) {
      HyperliquidSDKManager.instance = new HyperliquidSDKManager()
    }
    return HyperliquidSDKManager.instance
  }

  /**
   * 设置配置并重新创建实例（如果配置有变化）
   * @param config 配置参数
   */
  public setConfig(config: HyperliquidConfig = {}): void {
    if (this.configChanged(config)) {
      this.disconnect()
      this.currentConfig = { ...config }
      this.hyperliquid = null // 延迟创建
    }
  }

  /**
   * 获取 Hyperliquid 实例（懒加载）
   */
  public getHyperliquid(): Hyperliquid {
    if (!this.hyperliquid) {
      this.hyperliquid = new Hyperliquid(this.currentConfig)
      // 异步初始化，但不阻塞同步调用
      this.hyperliquid.connect().catch((error) => {
        console.warn('Hyperliquid 初始化失败:', error)
      })
    }
    return this.hyperliquid
  }

  /**
   * 检查配置是否发生变化
   */
  private configChanged(newConfig: HyperliquidConfig): boolean {
    if (!this.hyperliquid)
      return true

    const currentKeys = Object.keys(this.currentConfig)
    const newKeys = Object.keys(newConfig)

    if (currentKeys.length !== newKeys.length)
      return true

    return currentKeys.some((key) => {
      return this.currentConfig[key as keyof HyperliquidConfig] !== newConfig[key as keyof HyperliquidConfig]
    })
  }

  /**
   * 断开连接并重置实例
   */
  public disconnect(): void {
    if (this.hyperliquid) {
      this.hyperliquid.disconnect()
      this.hyperliquid = null
    }
  }

  /**
   * 重新连接
   */
  public reconnect(config?: HyperliquidConfig): void {
    this.disconnect()
    if (config) {
      this.setConfig(config)
    }
    // 下次调用 getHyperliquid 时会重新创建
  }
}

// 导出单例管理器实例
export const hyperliquidSDK = HyperliquidSDKManager.getInstance()

/**
 * 设置全局配置
 * @param config 配置参数
 */
export function setHyperliquidConfig(config: HyperliquidConfig): void {
  hyperliquidSDK.setConfig(config)
}

/**
 * 获取 Hyperliquid 主实例
 * @returns Hyperliquid 实例
 */
export async function getHyperliquid(): Promise<Hyperliquid> {
  const hyperliquid = hyperliquidSDK.getHyperliquid()
  await hyperliquid.connect()
  return hyperliquid
}

/**
 * 获取信息API客户端
 * @returns InfoAPI 实例
 */
export async function getInfoClient(options?: { address?: string, privateKey?: string }) {
  if (options) {
    setHyperliquidConfig({
      walletAddress: options.address,
      privateKey: options.privateKey,
    })
  }
  const hyperliquid = await getHyperliquid()
  return hyperliquid.info
}

/**
 * 获取交易API客户端
 * @returns ExchangeAPI 实例
 */
export async function getExchangeClient(options?: { address?: string, privateKey?: string }) {
  if (options) {
    setHyperliquidConfig({
      walletAddress: options.address,
      privateKey: options.privateKey,
    })
  }
  const hyperliquid = await getHyperliquid()
  return hyperliquid.exchange
}

/**
 * 获取WebSocket客户端
 * @returns WebSocketClient 实例
 */
export async function getWebSocketClient(options?: { address?: string, privateKey?: string }) {
  if (options) {
    setHyperliquidConfig({
      walletAddress: options.address,
      privateKey: options.privateKey,
    })
  }
  const hyperliquid = await getHyperliquid()
  return hyperliquid.ws
}
/**
 * 获取WebSocket订阅客户端
 * @returns WebSocketSubscriptions 实例
 */
export async function getWebSocketSubscriptions(options?: { address?: string, privateKey?: string }) {
  if (options) {
    setHyperliquidConfig({
      walletAddress: options.address,
      privateKey: options.privateKey,
    })
  }
  const hyperliquid = await getHyperliquid()
  await hyperliquid.ensureInitialized()
  await hyperliquid.connect()
  return hyperliquid.subscriptions
}

/**
 * 获取自定义操作客户端
 * @returns CustomOperations 实例
 */
export async function getCustomClient(options?: { address?: string, privateKey?: string }) {
  if (options) {
    setHyperliquidConfig({
      walletAddress: options.address,
      privateKey: options.privateKey,
    })
  }
  const hyperliquid = await getHyperliquid()
  return hyperliquid.custom
}

/**
 * 获取符号转换工具
 * @returns SymbolConversion 实例
 */
export async function getSymbolConversion() {
  const hyperliquid = await getHyperliquid()
  return hyperliquid.symbolConversion
}

// 导出一些便利方法
export const HyperSDK = {
  // 配置管理
  setConfig: setHyperliquidConfig,

  // 实例管理
  getInstance: () => hyperliquidSDK,
  getHyperliquid,
  disconnect: () => hyperliquidSDK.disconnect(),
  reconnect: (config?: HyperliquidConfig) => hyperliquidSDK.reconnect(config),

  // 客户端获取（同步）
  info: getInfoClient,
  exchange: getExchangeClient,
  ws: getWebSocketClient,
  subscriptions: getWebSocketSubscriptions,
  custom: getCustomClient,
  symbolConversion: getSymbolConversion,
}

// 默认导出
export default HyperSDK
